import React from 'react';
import FeatureScreenshot from '../FeatureScreenshot';
import FeatureFaqAccordion from '../FeatureFaqAccordion';
import styles from '../Features.module.css';

const SCREENSHOT_BASE = `${process.env.PUBLIC_URL || ''}/images/features/screenshots/themes`;
const screenshot = (filename) => `${SCREENSHOT_BASE}/${filename}.webp`;

const THEMES_FAQ = [
  {
    question: 'Puis-je avoir un thème de thread et un thème de personnage en même temps ?',
    answer: 'Oui. Le thread fournit le décor ; votre personnage conserve sa propre identité visuelle dans ses posts.',
  },
  {
    question: 'Les autres joueurs voient-ils mon thème de personnage ?',
    answer: 'Oui, sur vos posts RP et sur vos fiches.',
  },
  {
    question: 'Que se passe-t-il si je repasse sur « Par défaut » ?',
    answer: 'Le forum retrouve l\'apparence standard. Le filigrane thématique disparaît.',
  },
  {
    question: 'Un nouveau personnage a-t-il un thème par défaut ?',
    answer: (
      <>
        Oui : le thème <strong>Par défaut</strong> est appliqué tant que vous n&apos;en choisissez pas un autre.
      </>
    ),
  },
];

const ThemesFeature = () => (
  <div className={styles.articleContent}>
    <p>
      Earth Forum propose des <strong>thèmes visuels</strong> pour donner plus de personnalité
      à vos personnages et à vos discussions. Chaque thème change les <strong>couleurs</strong>,
      le <strong>filigrane</strong> en arrière-plan et certains détails de mise en page — sans
      modifier le contenu de vos textes.
    </p>

    <h2>Deux thèmes distincts</h2>
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th />
            <th>Thème du personnage</th>
            <th>Thème du thread</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>À quoi ça sert ?</th>
            <td>Habiller <em>votre</em> personnage</td>
            <td>Habiller <em>une discussion</em> (scène RP, sujet classique…)</td>
          </tr>
          <tr>
            <th>Qui peut le modifier ?</th>
            <td>Le joueur propriétaire du personnage</td>
            <td>L&apos;auteur du thread, ou un modérateur</td>
          </tr>
          <tr>
            <th>Où ça s&apos;applique ?</th>
            <td>Fiche de présentation, fiche interne, <strong>vos</strong> posts RP</td>
            <td>L&apos;ambiance générale de la page du thread (fond, filigrane, interface)</td>
          </tr>
          <tr>
            <th>Les posts RP des autres ?</th>
            <td>Chaque joueur garde le thème <strong>de son</strong> personnage</td>
            <td>Non concernés — ils restent au thème de leur personnage</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className={styles.summaryBox}>
      <p>
        <strong>En résumé :</strong> le thème du thread colore la « scène » ; le thème du
        personnage colore la façon dont <em>vous</em> apparaissez dedans.
      </p>
    </div>

    <h2>Les thèmes disponibles</h2>
    <h3>Par défaut</h3>
    <p>Style standard du forum : sobre, lisible, adapté à tous les univers.</p>
    <h3>Batman</h3>
    <p>
      Ambiance <strong>Gotham nocturne</strong> : tons sombres, accents dorés, lignes nettes
      et atmosphère dramatique.
    </p>
    <h3>Superman</h3>
    <p>
      Ambiance <strong>Metropolis héroïque</strong> : bleus profonds, touches rouges et dorées,
      filigrane lumineux et esprit « jour d&apos;action ».
    </p>
    <p><em>D&apos;autres thèmes pourront être ajoutés au fil du temps.</em></p>

    <h2>Thème du personnage</h2>
    <h3>Où le configurer ?</h3>
    <p>
      Depuis la page <strong>Mes personnages</strong>, ouvrez le menu d&apos;un personnage et
      choisissez <strong>Thème du personnage</strong>.
    </p>
    <FeatureScreenshot
      src={screenshot('themes-personnage-acces')}
      alt="Mes personnages — accès au thème"
      label="Capture à insérer — Liste « Mes personnages » avec le bouton ou menu « Thème du personnage »"
    />

    <h3>La page de personnalisation</h3>
    <p>Vous arrivez sur une page dédiée où vous pouvez :</p>
    <ol>
      <li><strong>Choisir un thème</strong> dans la liste à gauche</li>
      <li><strong>Prévisualiser</strong> le rendu à droite avant d&apos;enregistrer</li>
      <li><strong>Appliquer le thème</strong> une fois satisfait</li>
    </ol>
    <FeatureScreenshot
      src={screenshot('thèmes-personnage-page')}
      alt="Page thème du personnage — vue d'ensemble"
      label="Capture à insérer — Vue d'ensemble de la page « Thème du personnage »"
    />

    <h3>Les trois aperçus</h3>
    <p>La prévisualisation propose trois onglets :</p>
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Onglet</th>
            <th>Ce que vous voyez</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Post RP</strong></td>
            <td>Un exemple de message en jeu, avec la barre latérale personnage</td>
          </tr>
          <tr>
            <td><strong>Fiche de présentation</strong></td>
            <td>La fiche publique visible par les autres joueurs</td>
          </tr>
          <tr>
            <td><strong>Fiche interne</strong></td>
            <td>Votre fiche privée (statistiques, onglets…)</td>
          </tr>
        </tbody>
      </table>
    </div>
    <FeatureScreenshot
      src={screenshot('themes-personnage-apercu-rp')}
      alt="Aperçu — post RP"
      label="Capture à insérer — Onglet « Post RP »"
    />
    <FeatureScreenshot
      src={screenshot('themes-personnage-apercu-fiche')}
      alt="Aperçu — fiche de présentation"
      label="Capture à insérer — Onglet « Fiche de présentation »"
    />
    <FeatureScreenshot
      src={screenshot('themes-personnage-apercu-interne')}
      alt="Aperçu — fiche interne"
      label="Capture à insérer — Onglet « Fiche interne »"
    />

    <h3>Où le thème est visible en jeu ?</h3>
    <p>Une fois enregistré, le thème du personnage apparaît :</p>
    <ul>
      <li>Sur la <strong>fiche de présentation</strong></li>
      <li>Sur la <strong>fiche interne</strong> du personnage</li>
      <li>Sur <strong>chaque post RP</strong> écrit avec ce personnage</li>
      <li>En <strong>arrière-plan du site</strong> lorsque vous consultez ces pages (filigrane thématique)</li>
    </ul>
    <FeatureScreenshot
      src={screenshot('themes-personnage-post-en-jeu')}
      alt="Post RP en jeu"
      label="Capture à insérer — Post RP dans une vraie discussion"
    />
    <FeatureScreenshot
      src={screenshot('themes-personnage-fiche-en-jeu')}
      alt="Fiche de présentation en jeu"
      label="Capture à insérer — Fiche de présentation avec filigrane et couleurs du thème"
    />

    <h3>Thèmes de dialogue</h3>
    <p>
      Sur la même page, vous pouvez aussi gérer les <strong>thèmes de dialogue</strong> (couleurs
      des répliques dans vos posts). Ce réglage est <strong>indépendant</strong> du thème visuel
      du personnage.
    </p>
    <FeatureScreenshot
      src={screenshot('themes-personnage-dialogues')}
      alt="Thèmes de dialogue"
      label="Capture à insérer — Panneau « Thèmes de dialogue »"
    />

    <h2>Thème du thread</h2>
    <h3>À quoi ça sert ?</h3>
    <p>
      Le thème du thread habille <strong>la discussion elle-même</strong> : fond de page,
      filigrane, couleurs de l&apos;interface autour des messages. Idéal pour donner une ambiance
      à une scène RP <strong>sans imposer</strong> le même style aux personnages des autres joueurs.
    </p>
    <h3>Qui peut le modifier ?</h3>
    <ul>
      <li>L&apos;<strong>auteur</strong> du thread</li>
      <li>Un <strong>modérateur</strong></li>
    </ul>
    <p>
      Les fiches de présentation personnage ne possèdent pas de thème de thread : leur apparence
      est gérée par le <strong>thème du personnage</strong>.
    </p>

    <h3>Où le configurer ?</h3>
    <p>
      Ouvrez la discussion concernée. Dans le panneau de gestion (section <strong>Apparence</strong>),
      cliquez sur <strong>Thème du thread</strong>.
    </p>
    <FeatureScreenshot
      src={screenshot('themes-thread-acces')}
      alt="Accès au thème du thread"
      label="Capture à insérer — Panneau de gestion avec le lien « Thème du thread »"
    />

    <h3>La page de personnalisation</h3>
    <p>
      Comme pour les personnages, vous choisissez un thème, prévisualisez l&apos;en-tête de la
      discussion, puis enregistrez.
    </p>
    <FeatureScreenshot
      src={screenshot('themes-thread-page')}
      alt="Page thème du thread"
      label="Capture à insérer — Page « Thème du thread »"
    />

    <h3>Ce qui change (et ce qui ne change pas)</h3>
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Élément</th>
            <th>Affecté par le thème du thread ?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Fond de la page et filigrane</td>
            <td>✅ Oui</td>
          </tr>
          <tr>
            <td>En-tête et cartes de la discussion</td>
            <td>✅ Oui</td>
          </tr>
          <tr>
            <td>Posts RP des personnages</td>
            <td>❌ Non — chaque post garde le thème du personnage qui écrit</td>
          </tr>
          <tr>
            <td>Fiche de présentation liée à un personnage</td>
            <td>❌ Non — gérée par le thème du personnage</td>
          </tr>
        </tbody>
      </table>
    </div>
    <FeatureScreenshot
      src={screenshot('themes-thread-mixte')}
      alt="Thread thématisé avec posts aux styles variés"
      label="Capture à insérer — Thread thématisé avec des posts aux styles de personnages différents"
    />

    <h2>Exemples par thème</h2>
    <FeatureScreenshot
      src={screenshot('themes-exemple-batman')}
      alt="Exemple — thème Batman"
      label="Capture à insérer — Ambiance Batman"
    />
    <FeatureScreenshot
      src={screenshot('themes-exemple-superman')}
      alt="Exemple — thème Superman"
      label="Capture à insérer — Ambiance Superman"
    />

    <h2>Questions fréquentes</h2>
    <FeatureFaqAccordion items={THEMES_FAQ} />
  </div>
);

export default ThemesFeature;
